import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError, setTokens, clearTokens } from "@/lib/api";
import { CURRENCY } from "@/lib/format";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=user

  const applyUser = (u) => {
    setUser(u || false);
    if (u?.business?.currency) CURRENCY.symbol = u.business.currency;
  };

  const refetch = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applyUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  // One-time recovery: earlier builds swapped the read-only impersonation token into
  // localStorage, which could lock the Super Admin out. If a leftover read-only token
  // is sitting in localStorage, restore the backed-up admin token (or clear it).
  const recoverStuckSession = () => {
    try {
      const tok = localStorage.getItem("amden_access");
      if (tok && tok.split(".").length === 3) {
        const b = tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(b));
        if (payload && payload.ro) {
          const back = localStorage.getItem("amden_admin_backup");
          if (back) localStorage.setItem("amden_access", back);
          else localStorage.removeItem("amden_access");
        }
      }
    } catch { /* ignore malformed token */ }
    localStorage.removeItem("amden_admin_backup");
    localStorage.removeItem("amden_admin_refresh_backup");
  };

  useEffect(() => { recoverStuckSession(); refetch(); }, [refetch]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setTokens(data.access_token, data.refresh_token);
    return await refetch();
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setTokens(data.access_token, data.refresh_token);
    return await refetch();
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: applyUser, login, register, logout, refetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export { apiError };
